import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../../config/api';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import { FaEnvelope } from 'react-icons/fa';
import { EmailTemplate } from '../../../types/email';

const EmailTemplates: React.FC = () => {
	const { token } = useAuth();
	const navigate = useNavigate();
	const [templates, setTemplates] = useState<EmailTemplate[]>([]);
	const [filteredTemplates, setFilteredTemplates] = useState<EmailTemplate[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState<'all' | 'appointment' | 'follow-up' | 'referral' | 'general'>('all');
	const [selectedStatus, setSelectedStatus] = useState<'all' | 'active' | 'inactive'>('all');

	useEffect(() => {
		fetchTemplates();
	}, [token]);

	useEffect(() => {
		filterTemplates();
	}, [templates, searchTerm, selectedCategory, selectedStatus]);

	const fetchTemplates = async () => {
		if (!token) return;
		
		try {
			setLoading(true);
			const response = await apiCall(ENDPOINTS.EMAIL.TEMPLATES, {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				setTemplates(data.results || data || []);
			}
		} catch (error) {
			console.error('Error fetching templates:', error);
		} finally {
			setLoading(false);
		}
	};

	const filterTemplates = () => {
		let filtered = templates;

		// Filter by category
		if (selectedCategory !== 'all') {
			filtered = filtered.filter(t => t.category === selectedCategory);
		}

		// Filter by status
		if (selectedStatus === 'active') {
			filtered = filtered.filter(t => t.is_active === true);
		} else if (selectedStatus === 'inactive') {
			filtered = filtered.filter(t => t.is_active === false);
		}

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(t =>
				t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				t.subject_template.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}

		setFilteredTemplates(filtered);
	};

	const handleAdd = () => {
		navigate('/email/templates/editor');
	};

	const handleEdit = (template: EmailTemplate) => {
		navigate('/email/templates/editor', { state: { template } });
	};

	const handleDelete = async (id: string) => {
		if (!token) return;
		if (!window.confirm('Are you sure you want to delete this template?')) {
			return;
		}

		try {
			const response = await apiCall(`${ENDPOINTS.EMAIL.TEMPLATES}${id}/`, {
				method: 'DELETE',
			}, token);

			if (response.ok) {
				fetchTemplates();
			}
		} catch (error) {
			console.error('Error deleting template:', error);
			alert('Failed to delete template');
		}
	};

	return (
		<div className="p-6">
			{/* Header */}
			<div className="flex justify-between items-center mb-6">
				<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
					<FaEnvelope className="text-primary" />
					Email Templates
				</h1>
				<button
					onClick={handleAdd}
					className="btn btn-primary gap-2"
				>
					<FiPlus className="w-5 h-5" />
					Add Template
				</button>
			</div>

			{/* Search and Filter Bar */}
			<div className="bg-base-200 rounded-lg p-4 mb-6">
				<div className="flex items-center gap-6">
				{/* Search Input */}
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium whitespace-nowrap">Search:</span>
					<input
						type="text"
						placeholder="Search templates..."
						className="input input-bordered w-64"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>

				{/* Category Filter */}
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium whitespace-nowrap">Category:</span>
					<select
						className="select select-bordered w-48"
						value={selectedCategory}
						onChange={(e) => setSelectedCategory(e.target.value as any)}
					>
						<option value="all">All Categories</option>
						<option value="appointment">Appointment</option>
						<option value="follow-up">Follow-up</option>
						<option value="referral">Referral</option>
						<option value="general">General</option>
					</select>
				</div>

				{/* Status Filter */}
			<div className="flex items-center gap-4">
				<span className="text-sm font-medium whitespace-nowrap">Status:</span>
				<label className="flex items-center gap-2 cursor-pointer">
					<input
						type="radio"
						name="status"
						value="all"
						checked={selectedStatus === 'all'}
						onChange={(e) => setSelectedStatus(e.target.value as any)}
						className="radio radio-primary"
					/>
					<span className="text-sm">All</span>
				</label>
				<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="active"
								checked={selectedStatus === 'active'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">Active</span>
						</label>
						<label className="flex items-center gap-2 cursor-pointer">
							<input
								type="radio"
								name="status"
								value="inactive"
								checked={selectedStatus === 'inactive'}
								onChange={(e) => setSelectedStatus(e.target.value as any)}
								className="radio radio-primary"
							/>
							<span className="text-sm">Inactive</span>
						</label>
					</div>
				</div>
			</div>

			{/* Templates Table */}
			<div className="bg-base-100 rounded-lg shadow-lg overflow-hidden">
				{loading ? (
					<div className="flex justify-center items-center p-12">
						<span className="loading loading-spinner loading-lg"></span>
					</div>
				) : filteredTemplates.length === 0 ? (
					<div className="text-center p-12">
						<p className="text-lg opacity-60">
							No templates found
						</p>
						<p className="text-sm opacity-40 mt-2">
							{searchTerm || selectedCategory !== 'all'
								? 'Try adjusting your filters'
								: 'Click "Add Template" to create your first template'}
						</p>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table className="table table-zebra">
							<thead>
								<tr>
									<th>Name</th>
									<th>Category</th>
									<th>Subject Template</th>
									<th>Description</th>
									<th>Status</th>
									<th className="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredTemplates.map((template) => (
									<tr key={template.id} className="hover">
										<td className="font-medium">{template.name}</td>
										<td>
											<div className="badge badge-outline capitalize">
												{template.category || 'General'}
											</div>
										</td>
										<td className="max-w-md truncate text-sm opacity-70">
											{template.subject_template}
										</td>
										<td className="max-w-md truncate opacity-70">
											{template.description || '-'}
										</td>
										<td>
											{template.is_active ? (
												<span className="badge badge-success badge-sm">Active</span>
											) : (
												<span className="badge badge-ghost badge-sm">Inactive</span>
											)}
										</td>
										<td>
											<div className="flex justify-end gap-2">
												<button
													onClick={() => handleEdit(template)}
												className="btn btn-ghost btn-square"
												title="Edit"
											>
												<FiEdit2 className="w-4 h-4" />
											</button>
											<button
												onClick={() => handleDelete(template.id)}
												className="btn btn-ghost btn-square text-error"
													title="Delete"
												>
													<FiTrash2 className="w-4 h-4" />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{/* Results Count */}
			{!loading && (
				<div className="text-sm opacity-60 mt-4 text-center">
					Showing {filteredTemplates.length} of {templates.length} templates
				</div>
			)}
		</div>
	);
};

export default EmailTemplates;
